// Per-tenant PWA install identity.
//
// One frontend build serves every tenant, so the static manifest baked by vite-plugin-pwa gives
// every install the same name (the reason Blanchette and Echelon both installed as the same app).
// This applies the *resolved tenant's* identity at runtime, from the branding TenantContext already
// fetches. It covers both install surfaces:
//   * Android / Chrome / desktop  → the Web App Manifest `name`/`short_name`/`icons`
//   * iOS "Add to Home Screen"     → iOS ignores the manifest name; it uses the
//                                    apple-mobile-web-app-title meta (and <title>)
//
// GOTCHA: a manifest served from a blob: URL resolves relative member URLs (start_url, scope, icon
// src) against the blob origin, which is invalid — so every URL below is made absolute.

let _lastManifestBlobUrl = null

function _abs(url) {
  try {
    return new URL(url, window.location.origin).href
  } catch {
    return window.location.origin + '/'
  }
}

function _setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function _setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
  return el
}

/**
 * Apply a tenant's install identity to the current document.
 * @param {{name?:string, shortName?:string, themeColor?:string, iconUrl?:string}} branding
 */
export function applyPwaIdentity({ name, shortName, themeColor, iconUrl } = {}) {
  if (typeof document === 'undefined' || !document.head) return

  const appName = (name || 'Portal').trim() || 'Portal'
  // Home-screen labels get truncated by the OS anyway; keep short_name tight.
  const short = (shortName || appName).trim().slice(0, 30)
  const theme = themeColor || '#334155'
  const absIcon = iconUrl ? _abs(iconUrl) : null

  // 1) Web App Manifest (Android / Chrome / desktop install)
  try {
    const icons = []
    if (absIcon) {
      const type = absIcon.toLowerCase().includes('.png') ? 'image/png'
        : absIcon.toLowerCase().includes('.svg') ? 'image/svg+xml'
        : 'image/png'
      // sizes:'any' satisfies Chrome installability for SVG and single-size PNG logos.
      icons.push({ src: absIcon, sizes: 'any', type, purpose: 'any' })
      icons.push({ src: absIcon, sizes: 'any', type, purpose: 'maskable' })
    }
    const manifest = {
      name: appName,
      short_name: short,
      description: `${appName} — Construction Management & Timekeeping`,
      start_url: _abs('/'),
      scope: _abs('/'),
      display: 'standalone',
      background_color: '#ffffff',
      theme_color: theme,
      icons,
    }
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
    const url = URL.createObjectURL(blob)
    _setLink('manifest', url)
    if (_lastManifestBlobUrl) URL.revokeObjectURL(_lastManifestBlobUrl)
    _lastManifestBlobUrl = url
  } catch {
    /* manifest is best-effort; never break the app over install branding */
  }

  // 2) iOS "Add to Home Screen" — uses these, NOT the manifest name.
  _setMeta('apple-mobile-web-app-title', appName)
  _setMeta('application-name', appName)
  _setMeta('apple-mobile-web-app-capable', 'yes')
  _setMeta('mobile-web-app-capable', 'yes')
  _setMeta('theme-color', theme)
  if (absIcon) _setLink('apple-touch-icon', absIcon)

  // 3) THE BROWSER TAB — the one that was missed, and the one people actually look at.
  //
  // The install icon and the iOS home-screen icon were both being swapped per tenant; the
  // `rel="icon"` link index.html ships was not. So every tenant kept the static fallback in the tab
  // on every screen forever, which is how it was reported: "a B shows up as the App image and the
  // website emblem regardless of the tenant".
  //
  // `type` is REMOVED rather than set. The earlier attempt at this (TenantContext) asserted
  // `image/x-icon` onto whatever the tenant had uploaded — and a tenant logo is a PNG or an SVG,
  // never an ICO — handing the browser a link whose declared type contradicts its bytes, which it
  // may reasonably ignore while continuing to paint the fallback. With no `type` the browser
  // sniffs, which is the honest choice when the bytes are user-uploaded and their format is not
  // ours to promise.
  if (absIcon) {
    const el = _setLink('icon', absIcon)
    if (el) el.removeAttribute('type')
  }
}
